import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { artAssets } from "../data/site";
import { SponsorStrip } from "./SponsorStrip";

const lines = ["THE NETWORK", "IS NOT A CAGE.", "IT'S A WARZONE."];
/* one hero transmission — the official upload, on loop */
const HERO_CLIP = {
  webm: "/assets/hero-main.webm",
  mp4: "/assets/hero-main.mp4",
  poster: "/assets/hero-main-poster.jpg",
  label: "Dolo: Wasteland Runner — official hero transmission",
};
const networkLines = [
  { left: "12%", top: "18%", width: "34%", rotate: "-18deg" },
  { left: "48%", top: "28%", width: "42%", rotate: "12deg" },
  { left: "6%", top: "68%", width: "54%", rotate: "8deg" },
  { left: "62%", top: "72%", width: "30%", rotate: "-24deg" },
];

export function HeroSection() {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 20 });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 20 });
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const mutedRef = useRef(true);

  const toggleMute = async () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !muted;
    mutedRef.current = video.muted;
    setMuted(video.muted);
    if (!video.muted) {
      try {
        await video.play();
      } catch {
        setMuted(true);
        mutedRef.current = true;
        video.muted = true;
      }
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    // iPad/Safari + strict desktop policies need muted on the element itself;
    // preserve the viewer's sound choice across reloads
    video.muted = mutedRef.current;
    video.defaultMuted = mutedRef.current;
    if (mutedRef.current) video.setAttribute("muted", "");
    const kick = () => video.play().catch(() => {
      // sound-on autoplay can still be blocked — fall back to muted
      if (!video.muted) {
        video.muted = true;
        mutedRef.current = true;
        setMuted(true);
        video.play().catch(() => {});
      }
    });
    video.addEventListener("canplay", kick, { once: true });
    kick();
    // if the browser stalled the autostart (gate overlay up, tab hidden, slow
    // network), re-kick when the gate lifts or the tab comes back into view
    const relaunch = () => {
      if (!document.hidden) videoRef.current?.play().catch(() => {});
    };
    window.addEventListener("dolo-radio-autostart", relaunch);
    document.addEventListener("visibilitychange", relaunch);
    return () => {
      video.removeEventListener("canplay", kick);
      window.removeEventListener("dolo-radio-autostart", relaunch);
      document.removeEventListener("visibilitychange", relaunch);
    };
  }, []);

  const handleMouseMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    mouseX.set((event.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((event.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section id="top" className="hero-section" onMouseMove={handleMouseMove} onMouseLeave={() => { mouseX.set(0); mouseY.set(0); }} data-testid="hero-section">
      <div
        className="hero-city hero-city-art"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.42), rgba(0, 0, 0, 0.72)), url(${artAssets.cityPanorama})`,
        }}
      />
      <div className="hero-vignette" />
      {networkLines.map((line, index) => (
        <span
          className="network-line"
          style={{ left: line.left, top: line.top, width: line.width, transform: `rotate(${line.rotate})` }}
          key={index}
        />
      ))}

      <div className="hero-inner">
        <div className="hero-copy">          <motion.p
            className="hero-kicker"
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            DOLO: WASTELAND RUNNER / VOL. 1
          </motion.p>
          <h1 className="hero-title" aria-label="The network is not a cage. It's a warzone.">
            {lines.map((line, index) => (
              <span className="mask-line" key={line}>
                <motion.span
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + index * 0.13, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            className="hero-subtitle"
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
          >
            Experience Volume 1 of the post-apocalyptic samurai cyber-saga. Survive the wasteland.
          </motion.p>
        </div>

        <motion.div
          className="hero-visual hero-video-shell"
          style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
          initial={{ opacity: 0, scale: 0.94, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
          data-testid="hero-video-stack"
        >
          <div className="video-shadow-layer video-shadow-back" />
          <div className="video-shadow-layer video-shadow-mid" />
          <div className="hero-video-frame">
            <video
              ref={videoRef}
              poster={HERO_CLIP.poster}
              className="cover-video"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              aria-label={HERO_CLIP.label}
              data-testid="hero-cover-video"
            >
              <source src={HERO_CLIP.webm} type="video/webm" />
              <source src={HERO_CLIP.mp4} type="video/mp4" />
            </video>
            <button
              type="button"
              className="video-mute-button"
              onClick={toggleMute}
              data-testid="hero-video-mute-button"
              aria-label={muted ? "Unmute hero video" : "Mute hero video"}
              aria-pressed={!muted}
            >
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              <span>{muted ? "MUTED" : "SOUND ON"}</span>
            </button>
          </div>
          <div className="hero-scanline" />
        </motion.div>

        <SponsorStrip />
      </div>

      <div className="hero-bottom-strip" data-testid="hero-status-strip">
        <span>SIGNAL: ACTIVE</span>
        <span>VIDEO: LIVE</span>
        <span>ARCHIVE: VOL. 01</span>
      </div>
    </section>
  );
}
